import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Switch,
  Button,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXTwitter,
  faYoutube,
  faInstagram,
  faReddit,
  faQuora,
  faPinterest,
  faFacebook,
} from "@fortawesome/free-brands-svg-icons";
import { faGlobe } from "@fortawesome/free-solid-svg-icons";
import { modelsApi } from "../../../../../API/axiosInstance";
import { PLATFORMS } from "./TopicSelect";

import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify"; // Import Toastify
import DeleteTopicDialog from "./TopicDeleteDialog";
import DurationPicker from "./DurationPicker";
import { MyContext } from "../../../../../MyContextApi";

const getModelsWsUrl = () => {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/models/models`;
};

// ── Platform → Font Awesome icon + subtle color tokens ──────────
// Each platform has: icon, accent (icon/text), tint (soft bg), border.
const PLATFORM_META = {
  twitter: {
    icon: faXTwitter,
    accent: "#1f2937",
    tint: "#f3f4f6",
    border: "#d8dbe0",
    label: "Twitter / X",
  },
  x: {
    icon: faXTwitter,
    accent: "#1f2937",
    tint: "#f3f4f6",
    border: "#d8dbe0",
    label: "X",
  },
  youtube: {
    icon: faYoutube,
    accent: "#c4302b",
    tint: "#fdf0ef",
    border: "#f3cfcc",
    label: "YouTube",
  },
  instagram: {
    icon: faInstagram,
    accent: "#c13584",
    tint: "#fcf0f7",
    border: "#f0cfe3",
    label: "Instagram",
  },
  reddit: {
    icon: faReddit,
    accent: "#d93a00",
    tint: "#fdf1ec",
    border: "#f4d2c4",
    label: "Reddit",
  },
  quora: {
    icon: faQuora,
    accent: "#a82400",
    tint: "#fbefec",
    border: "#eecdc4",
    label: "Quora",
  },
  pinterest: {
    icon: faPinterest,
    accent: "#bd081c",
    tint: "#fceef0",
    border: "#f1ccd1",
    label: "Pinterest",
  },
  facebook: {
    icon: faFacebook,
    accent: "#1877f2",
    tint: "#eef4fe",
    border: "#cfe0fb",
    label: "Facebook",
  },
};

const getPlatformMeta = (p) => {
  const key = (p || "").toLowerCase();
  return (
    PLATFORM_META[key] || {
      icon: faGlobe,
      accent: "#6b7280",
      tint: "#f3f4f6",
      border: "#e5e7eb",
      label: p ? p.charAt(0).toUpperCase() + p.slice(1) : p,
    }
  );
};

const EditModal = ({ open, onClose, sessionData, onSave }) => {
  const [duration, setDuration] = useState("00:00:00");
  const [originalData, setOriginalData] = useState({});
  const { formData, setFormData } = useContext(MyContext) || {}; // defensive

  const [processing, setProcessing] = useState(false);
  const [socket, setSocket] = useState(null);

  const [newKeyword, setNewKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const token = localStorage.getItem("Token");

  const [activeTab, setActiveTab] = useState(0);
  const [platformPriorityOrder, setPlatformPriorityOrder] = useState([]);
  const [originalPlatformPriorityOrder, setOriginalPlatformPriorityOrder] =
    useState([]);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const dragIndexRef = useRef(null);

  const dragKeywordIndexRef = useRef(null);
  const [draggingKeywordIndex, setDraggingKeywordIndex] = useState(null);
  const [dragOverKeywordIndex, setDragOverKeywordIndex] = useState(null);

  // Full pool of platforms to choose from (expand "all").
  const allPlatforms = [...PLATFORMS];

  // Initialize form data when modal opens
  useEffect(() => {
    if (sessionData && open) {
      const initialData = {
        query: sessionData.topic || "",
        booleanQuery: sessionData.agent_result?.boolean_query || "",
        type: sessionData.type || "section",
        platform: sessionData.platform || [],
        status: sessionData.status || "stop",
        keywords: sessionData.agent_result?.keywords || [],
      };

      if (typeof setFormData === "function") {
        setFormData(initialData);
      } else {
        console.warn("setFormData is not available from context");
      }
      setOriginalData(initialData);
      setLoadError(null);
      setActiveTab(0);

      const effectivePlatforms = (initialData.platform || []).includes("all")
        ? [...PLATFORMS]
        : [...(initialData.platform || [])];
      const initialPriority =
        sessionData.platform_priority_order || effectivePlatforms;
      setPlatformPriorityOrder(initialPriority);
      setOriginalPlatformPriorityOrder(initialPriority);
    }
  }, [sessionData, open]);

  // Mark error if formData isn't present when modal is open
  useEffect(() => {
    if (open && !formData) {
      setLoadError("Form data failed to load. Please reopen the modal.");
    } else {
      setLoadError(null);
    }
  }, [open, formData]);

  const isLocked = processing || isLoading || !!loadError;

  const handleTypeChange = (event) => {
    if (typeof setFormData !== "function") return;
    setFormData((prev = {}) => ({ ...prev, type: event.target.value }));
  };

  const handleStatusChange = (event) => {
    if (typeof setFormData !== "function") return;
    setFormData((prev = {}) => ({
      ...prev,
      status: event.target.checked ? "start" : "stop",
    }));
  };

  // Central setter that keeps formData.platform and priority order in sync.
  const applyPlatformSelection = (selectedPlatforms) => {
    if (typeof setFormData !== "function") return;
    if (selectedPlatforms.length === 0) {
      toast.warn("At least one platform must be selected.");
      return;
    }
    setFormData((prev = {}) => ({ ...prev, platform: selectedPlatforms }));

    const effective = selectedPlatforms.includes("all")
      ? [...PLATFORMS]
      : [...selectedPlatforms];
    setPlatformPriorityOrder((prev) => {
      const kept = prev.filter((p) => effective.includes(p));
      const added = effective.filter((p) => !prev.includes(p));
      return [...kept, ...added];
    });
  };

  // The currently-selected platforms, normalized (expand "all").
  const selectedPlatforms = (formData?.platform ?? []).includes("all")
    ? [...PLATFORMS]
    : (formData?.platform ?? []);

  // Pool = everything not yet selected.
  const availablePlatforms = allPlatforms.filter(
    (p) => !selectedPlatforms.includes(p),
  );

  // Add from pool → appended; priority order picks it up via applyPlatformSelection.
  const handleAddPlatform = (platform) => {
    if (isLocked) return;
    applyPlatformSelection([...selectedPlatforms, platform]);
  };

  // Remove from priority row → back to pool.
  const handleRemovePlatform = (platform) => {
    if (isLocked) return;
    const next = selectedPlatforms.filter((p) => p !== platform);
    if (next.length === 0) {
      toast.warn("At least one platform must be selected.");
      return;
    }
    applyPlatformSelection(next);
  };

  const handleDragStart = (index) => {
    dragIndexRef.current = index;
    setDraggingIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (dropIndex) => {
    const fromIndex = dragIndexRef.current;
    dragIndexRef.current = null;
    setDragOverIndex(null);
    setDraggingIndex(null);
    if (fromIndex === null || fromIndex === dropIndex) return;
    const reordered = [...platformPriorityOrder];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setPlatformPriorityOrder(reordered);
  };

  const handleDeleteKeyword = (keywordToDelete) => {
    if (typeof setFormData !== "function") return;
    setFormData((prev = {}) => ({
      ...prev,
      keywords: (prev.keywords || []).filter((k) => k !== keywordToDelete),
    }));
  };

  const handleKeywordDragStart = (index) => {
    dragKeywordIndexRef.current = index;
    setDraggingKeywordIndex(index);
  };

  const handleKeywordDragOver = (e, index) => {
    e.preventDefault();
    setDragOverKeywordIndex(index);
  };

  const handleKeywordDrop = (dropIndex) => {
    const fromIndex = dragKeywordIndexRef.current;
    dragKeywordIndexRef.current = null;
    setDragOverKeywordIndex(null);
    setDraggingKeywordIndex(null);
    if (fromIndex === null || fromIndex === dropIndex) return;
    const keywords = [...(formData?.keywords || [])];
    const [moved] = keywords.splice(fromIndex, 1);
    keywords.splice(dropIndex, 0, moved);
    if (typeof setFormData === "function") {
      setFormData((prev = {}) => ({ ...prev, keywords }));
    }
  };

  const handleAddKeyword = () => {
    try {
      if (!newKeyword.trim()) return;
      const currentKeywords = (formData && formData.keywords) || [];
      const candidates = newKeyword
        .split(",")
        .map((k) => k.trim())
        .filter((k) => k.length > 0);
      const toAdd = candidates.filter((k) => !currentKeywords.includes(k));
      const dupes = candidates.filter((k) => currentKeywords.includes(k));
      if (dupes.length > 0) toast.info(`Already exists: ${dupes.join(", ")}`);
      if (toAdd.length === 0) return;
      if (typeof setFormData !== "function") return;
      setFormData((prev = {}) => ({
        ...prev,
        keywords: [...(prev.keywords || []), ...toAdd],
      }));
      setNewKeyword("");
    } catch (err) {
      console.error("Error adding keyword:", err);
      toast.error("Failed to add keyword");
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddKeyword();
    }
  };

  const arraysEqual = (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
  };

  const getChangedFields = () => {
    const changes = {};
    const fd = formData || {};
    const od = originalData || {};

    // 🔹 Track topic (query) changes
    if ((fd.query || "") !== (od.query || "")) {
      changes.topic = fd.query || "";
    }

    if ((fd.type || "section") !== (od.type || "section"))
      changes.type = fd.type;
    if (!arraysEqual(fd.platform || [], od.platform || [])) {
      const selected = fd.platform || [];
      changes.platform = selected.includes("all") ? [...PLATFORMS] : selected;
    }
    if (!arraysEqual(platformPriorityOrder, originalPlatformPriorityOrder)) {
      changes.platform_priority_order = platformPriorityOrder;
    }
    if ((fd.status || "stop") !== (od.status || "stop"))
      changes.status = fd.status;

    // 🔹 Compare old and new keywords
    const oldKeywords = od.keywords || [];
    const newKeywords = fd.keywords || [];

    const keywordsAdded = newKeywords.filter((k) => !oldKeywords.includes(k));
    const keywordsRemoved = oldKeywords.filter((k) => !newKeywords.includes(k));
    const keywordsReordered =
      keywordsAdded.length === 0 &&
      keywordsRemoved.length === 0 &&
      !arraysEqual(oldKeywords, newKeywords);

    if (keywordsAdded.length > 0 || keywordsRemoved.length > 0) {
      changes.keywords_add = keywordsAdded;
      changes.keywords_remove = keywordsRemoved;
    }
    if (keywordsReordered) {
      changes.keywords_order = newKeywords;
    }

    return changes;
  };

  const handleSave = async () => {
    if (!formData) {
      toast.error("Form data not available. Cannot save.");
      return;
    }

    if (!formData.platform || formData.platform.length === 0) {
      toast.warn("At least one platform must be selected.");
      return;
    }

    const changedFields = getChangedFields();
    const newStatus = formData.status;

    try {
      // If only status changed or status included
      if ("status" in changedFields) {
        try {
          const response = await modelsApi.post(
            `/topics/scrap/${sessionData._id}`,
            { action: newStatus, duration: duration },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
          const data = response.data;
          if (data.status === "success") {
            toast.success(data.message || "Status updated");
          } else if (data.status === "error") {
            toast.error(data.message || "Failed to update status");
          } else if (data.detail) {
            toast.error(data.detail);
          } else {
            toast.info("Unexpected response from server");
          }
        } catch (error) {
          if (error.response) {
            toast.error(
              error.response.data.message ||
                error.response.data.detail ||
                "Server error",
            );
          } else if (error.request) {
            toast.error(
              "Network error. Please check connection and try again.",
            );
          } else {
            toast.error("An unexpected error occurred. Please try again.");
          }
        }
      }

      // 🔹 If there are *other changes* (type, platform, keywords), send PATCH
      if (Object.keys(changedFields).length > 0) {
        setIsLoading(true);
        try {
          const response = await modelsApi.patch(
            `/topics/edit/${sessionData._id}`,
            { ...changedFields },
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          if (onSave) onSave(response.data);
          toast.success("Topic updated");
        } catch (err) {
          if (err.response) {
            toast.error(
              err.response.data.message ||
                err.response.data.detail ||
                "Failed to update topic",
            );
          } else if (err.request) {
            toast.error("Network error while saving changes");
          } else {
            toast.error("Unexpected error while saving");
          }
        } finally {
          setIsLoading(false);
        }
      }

      onClose(); // close modal after saving
    } catch (error) {
      console.error("Save error:", error);
      if (error.response) {
        toast.error(
          error.response.data.message ||
            error.response.data.detail ||
            "Server error",
        );
      } else if (error.request) {
        toast.error("Network error. Please check connection and try again.");
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setNewKeyword("");
    onClose();
  };

  const hasChanges = () => {
    try {
      return Object.keys(getChangedFields()).length > 0;
    } catch (err) {
      console.error("Error checking changes:", err);
      return false;
    }
  };

  // Modern inline styles
  const baseFont = '"Host Grotesk", sans-serif';

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "560px",
    maxWidth: "92vw",
    maxHeight: "90vh",
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0px 24px 48px rgba(0,0,0,0.12), 0px 8px 24px rgba(0,0,0,0.08)",
    border: "1px solid rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    fontFamily: baseFont,
  };

  const headerStyle = {
    padding: "18px 22px",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#f9fafb",
  };

  const titleStyle = {
    margin: 0,
    fontWeight: 700,
    fontSize: "20px",
    color: "#111827",
    letterSpacing: "-0.01em",
    fontFamily: baseFont,
  };

  const headerIconBtn = {
    minWidth: "auto",
    padding: "6px",
    borderRadius: "10px",
    color: "#6b7280",
    transition: "all 0.2s ease",
    fontFamily: baseFont,
  };

  const contentStyle = {
    padding: "22px",
    maxHeight: "calc(90vh - 140px)",
    overflowY: "auto",
    backgroundColor: "#ffffff",
    fontFamily: baseFont,
  };

  const sectionStyle = {
    marginBottom: "20px",
    fontFamily: baseFont,
  };

  const sectionLabel = {
    fontSize: "14px",
    fontWeight: 700,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "10px",
    fontFamily: baseFont,
  };

  const dividerStyle = { margin: "18px 0", borderColor: "rgba(0,0,0,0.06)" };

  const radioRoot = {
    display: "flex",
    gap: "8px",
    fontFamily: baseFont,
  };
  const switchRowParent = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontFamily: baseFont,
    flexDirection: "column",
  };
  const switchRow = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontFamily: baseFont,
    flexDirection: "column",
  };

  const chipContainerStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    maxHeight: "250px",
    overflowY: "auto",
    padding: "16px",
    backgroundColor: "#f9fafb",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    borderRadius: "12px",
    fontFamily: baseFont,
  };

  const footerStyle = {
    padding: "14px 22px",
    borderTop: "1px solid rgba(0,0,0,0.08)",
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    backgroundColor: "#f9fafb",
    fontFamily: baseFont,
  };

  // Button styles
  const outlinedBtn = (disabled, redActive) => ({
    color: disabled
      ? "#9ca3af"
      : redActive
        ? "#ef4444" // 🔴 red text when active
        : "#6366f1", // default indigo

    borderColor: disabled ? "#e5e7eb" : redActive ? "#ef4444" : "#6366f1",
    borderWidth: "2px",
    backgroundColor: "transparent",
    fontFamily: baseFont,
    fontWeight: 700,
    fontSize: "14px",
    padding: "10px 18px",
    textTransform: "none",
    borderRadius: "12px",
    transition: "all 0.2s ease",
    boxShadow: "none",
  });

  const containedBtn = (disabled) => ({
    backgroundColor: disabled ? "#e5e7eb" : "#6366f1",
    color: disabled ? "#9ca3af" : "#ffffff",
    fontFamily: baseFont,
    fontWeight: 700,
    fontSize: "14px",
    padding: "10px 18px",
    textTransform: "none",
    borderRadius: "12px",
    border: "none",
    boxShadow: disabled ? "none" : "0px 4px 12px rgba(99,102,241,0.3)",
    transition: "all 0.2s ease",
    cursor: disabled ? "not-allowed" : "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  });

  const fetchUpdatedTopic = async () => {
    try {
      if (!sessionData?._id) return;
      const response = await modelsApi.get(`/topics`, {
        params: { topic_id: sessionData._id }, // ✅ query param
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const updatedData = response.data;
      const refreshedKeywords = updatedData.agent_result?.keywords || [];
      if (typeof setFormData === "function") {
        setFormData((prev = {}) => ({ ...prev, keywords: refreshedKeywords }));
      }
    } catch (error) {
      console.error("Failed to refresh topic data:", error);
      toast.error("Failed to refresh topic data");
    }
  };

  useEffect(() => {
    if (!sessionData?._id || !open) return;

    let ws;
    try {
      ws = new WebSocket(`${getModelsWsUrl()}/status/${sessionData._id}`);
      setSocket(ws);
      setProcessing(true);

      ws.onopen = () =>
        (ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            if (data.status === "completed") {
              setProcessing(false);
              ws.close();
              fetchUpdatedTopic(); // 🔁 Refresh topic
            }
          } catch (err) {
            console.error("WS Parse error:", err);
          }
        });
      ws.onerror = (err) => console.error("WebSocket error:", err);
      ws.onclose = () => {
        setProcessing(false);
      };
    } catch (err) {
      console.error("Failed to create WebSocket:", err);
      setProcessing(false);
    }

    return () => {
      try {
        ws && ws.close();
      } catch (e) {
        console.warn("Error closing WS:", e);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionData?._id, open]);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="edit-session-modal"
      >
        <Box
          style={{
            ...modalStyle,
            width: "1000px", // Make modal much wider
            maxWidth: "95vw", // Responsive on smaller screens
          }}
        >
          {/* Header */}
          <Box style={headerStyle}>
            <Typography style={titleStyle}>Edit Topic</Typography>
            <Button
              onClick={handleClose}
              style={headerIconBtn}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#f3f4f6";
                e.currentTarget.style.color = "#374151";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = "#6b7280";
              }}
            >
              <CloseIcon />
            </Button>
          </Box>

          {/* If form data failed to load, show inline error banner */}
          {loadError ? (
            <Box
              style={{
                padding: "14px 22px",
                backgroundColor: "#fff7f7",
                borderBottom: "1px solid rgba(239,68,68,0.06)",
              }}
            >
              <Typography style={{ color: "#b91c1c", fontWeight: 700 }}>
                {loadError}
              </Typography>
              <Typography style={{ color: "#7f1d1d", marginTop: 6 }}>
                Try closing and reopening the modal. Actions are disabled until
                data loads.
              </Typography>
            </Box>
          ) : null}

          {/* Tabs */}
          <Box
            style={{
              borderBottom: "1px solid rgba(0,0,0,0.08)",
              paddingLeft: "22px",
              paddingRight: "22px",
              backgroundColor: "#f9fafb",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{
                minHeight: "42px",
                "& .MuiTab-root": {
                  fontFamily: baseFont,
                  fontWeight: 600,
                  fontSize: "13px",
                  textTransform: "none",
                  color: "#6b7280",
                  minHeight: "42px",
                  padding: "0 4px",
                  marginRight: "20px",
                  "&.Mui-selected": { color: "#6366f1" },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#6366f1",
                  height: "2px",
                },
              }}
            >
              <Tab label="General" />
              <Tab label="Platform Priority" />
            </Tabs>
          </Box>

          {/* Content */}
          <Box style={contentStyle}>
            {/* ── General Tab ── */}
            {activeTab === 0 && (
              <Box
                style={{
                  display: "flex",
                  gap: "32px",
                  alignItems: "flex-start",
                }}
              >
                {/* Left Column */}
                <Box style={{ flex: "1", minWidth: "0" }}>
                  {/* Query */}
                  <Box style={sectionStyle}>
                    <Typography style={sectionLabel}>Topic</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      disabled={isLocked}
                      value={formData?.query ?? ""}
                      onChange={(e) =>
                        typeof setFormData === "function" &&
                        setFormData((prev = {}) => ({
                          ...prev,
                          query: e.target.value,
                        }))
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "44px",
                          fontFamily: baseFont,
                          fontSize: "14px",
                          borderRadius: "12px",
                          backgroundColor: "#fafafa",
                          "& fieldset": { borderColor: "rgba(0,0,0,0.12)" },
                          "&:hover fieldset": { borderColor: "#6366f1" },
                          "&.Mui-focused fieldset": {
                            borderColor: "#6366f1",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          fontFamily: baseFont,
                          fontSize: "14px",
                        },
                      }}
                    />
                  </Box>

                  <Divider style={dividerStyle} />

                  {/* Type */}
                  <Box style={sectionStyle}>
                    <FormControl
                      component="fieldset"
                      style={{ fontFamily: baseFont }}
                    >
                      <FormLabel component="legend" style={sectionLabel}>
                        Objective
                      </FormLabel>
                      <RadioGroup
                        value={formData?.type ?? "section"}
                        onChange={handleTypeChange}
                        row
                        style={radioRoot}
                      >
                        <FormControlLabel
                          value="section"
                          control={
                            <Radio
                              disabled={isLocked}
                              sx={{
                                color: "#5855eb",
                                "&.Mui-checked": { color: "#5855eb" },
                              }}
                            />
                          }
                          label="Section"
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontFamily: "Host Grotesk, sans-serif",
                            },
                          }}
                        />
                        <FormControlLabel
                          value="sentiment"
                          control={
                            <Radio
                              disabled={isLocked}
                              sx={{
                                color: "#5855eb",
                                "&.Mui-checked": { color: "#5855eb" },
                              }}
                            />
                          }
                          label="Sentiment"
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontFamily: "Host Grotesk, sans-serif",
                            },
                          }}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  {/* Status */}
                  <Box style={switchRowParent}>
                    <Box style={switchRow}>
                      <Box
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontFamily: baseFont,
                        }}
                      >
                        <Typography
                          style={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#374151",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            fontFamily: baseFont,
                          }}
                        >
                          Status
                        </Typography>
                        <Typography
                          style={{
                            color: "#6b7280",
                            fontSize: "13px",
                            fontFamily: baseFont,
                          }}
                        >
                          Stop
                        </Typography>
                        <Switch
                          checked={formData?.status === "start"}
                          onChange={handleStatusChange}
                          color="primary"
                          disabled={isLocked}
                        />
                        <Typography
                          style={{
                            color: "#6b7280",
                            fontSize: "13px",
                            fontFamily: baseFont,
                          }}
                        >
                          Start
                        </Typography>
                      </Box>
                      <Box style={switchRow}>
                        <DurationPicker
                          onChange={(value) => setDuration(value)}
                          disabled={isLocked || formData?.status === "stop"}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Vertical Divider */}
                <Divider
                  orientation="vertical"
                  flexItem
                  style={{ backgroundColor: "#e5e7eb", width: "1px" }}
                />

                {/* Right Column - Keywords */}
                <Box
                  style={{ flex: "1.2", minWidth: "0", paddingLeft: "16px" }}
                >
                  <Box style={{ ...sectionStyle, marginTop: "0" }}>
                    <Typography style={sectionLabel}>
                      Keywords ({(formData?.keywords || []).length})
                    </Typography>

                    <Box style={{ marginTop: "10px" }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Add keyword(s), comma-separated..."
                        value={newKeyword}
                        onChange={(e) => setNewKeyword(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={isLocked}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            fontFamily: baseFont,
                            fontSize: "14px",
                            borderRadius: "12px",
                            backgroundColor: "#fafafa",
                            "& fieldset": { borderColor: "rgba(0,0,0,0.12)" },
                            "&:hover fieldset": { borderColor: "#6366f1" },
                            "&.Mui-focused fieldset": {
                              borderColor: "#6366f1",
                              borderWidth: "2px",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            fontFamily: baseFont,
                            fontSize: "14px",
                          },
                        }}
                        InputProps={{
                          endAdornment: (
                            <Button
                              size="small"
                              onClick={handleAddKeyword}
                              disabled={
                                !newKeyword.trim() || isLoading || !!loadError
                              }
                              style={outlinedBtn(
                                !newKeyword.trim() || isLoading || !!loadError,
                              )}
                              onMouseOver={(e) => {
                                if (
                                  newKeyword.trim() &&
                                  !isLoading &&
                                  !loadError
                                ) {
                                  e.currentTarget.style.backgroundColor =
                                    "#f0f9ff";
                                  e.currentTarget.style.borderColor = "#5855eb";
                                  e.currentTarget.style.color = "#5855eb";
                                }
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                                e.currentTarget.style.borderColor = "#6366f1";
                                e.currentTarget.style.color = "#6366f1";
                              }}
                            >
                              Add
                            </Button>
                          ),
                        }}
                      />
                    </Box>

                    <Box
                      style={{
                        ...chipContainerStyle,
                        marginTop: "12px",
                        minHeight: "200px",
                        maxHeight: "400px",
                        overflowY: "auto",
                        position: "relative",
                      }}
                    >
                      {processing ? (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(255,255,255,0.6)",
                            backdropFilter: "blur(2px)",
                            borderRadius: "8px",
                            zIndex: 2,
                          }}
                        >
                          <CircularProgress
                            size={36}
                            thickness={4}
                            color="primary"
                          />
                        </Box>
                      ) : null}

                      {(formData?.keywords || []).length > 0 ? (
                        <Box
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                          }}
                        >
                          {(formData?.keywords || []).map((keyword, index) => {
                            const isDraggingKw = draggingKeywordIndex === index;
                            const isDragOverKw =
                              dragOverKeywordIndex === index &&
                              draggingKeywordIndex !== index;
                            return (
                              <Box
                                key={index}
                                draggable={!isLocked}
                                onDragStart={() =>
                                  handleKeywordDragStart(index)
                                }
                                onDragOver={(e) =>
                                  handleKeywordDragOver(e, index)
                                }
                                onDrop={() => handleKeywordDrop(index)}
                                onDragEnd={() => {
                                  dragKeywordIndexRef.current = null;
                                  setDragOverKeywordIndex(null);
                                  setDraggingKeywordIndex(null);
                                }}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "5px",
                                  backgroundColor: isDragOverKw
                                    ? "#c7d2fe"
                                    : "#e0e7ff",
                                  border: `1.5px solid ${isDragOverKw ? "#6366f1" : "rgba(99,102,241,0.25)"}`,
                                  borderRadius: "999px",
                                  padding: "7px 12px 7px 9px",
                                  cursor: isLocked ? "default" : "grab",
                                  opacity: isDraggingKw ? 0.45 : 1,
                                  transform: isDragOverKw
                                    ? "scale(1.03)"
                                    : "none",
                                  transition: "all 0.15s ease",
                                  userSelect: "none",
                                }}
                              >
                                <DragIndicatorIcon
                                  style={{
                                    fontSize: "15px",
                                    color: "#6366f1",
                                    opacity: 0.45,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  style={{
                                    fontFamily: baseFont,
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#3730a3",
                                    lineHeight: 1,
                                  }}
                                >
                                  {keyword}
                                </Typography>
                                <Box
                                  onClick={() =>
                                    !isLocked && handleDeleteKeyword(keyword)
                                  }
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: isLocked ? "default" : "pointer",
                                    color: "#6366f1",
                                    opacity: 0.55,
                                    marginLeft: "2px",
                                    flexShrink: 0,
                                  }}
                                  onMouseOver={(e) => {
                                    if (!isLocked)
                                      e.currentTarget.style.opacity = "1";
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.opacity = "0.55";
                                  }}
                                >
                                  <CloseIcon style={{ fontSize: "14px" }} />
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography
                          style={{
                            textAlign: "center",
                            padding: "12px 0",
                            color: "#6b7280",
                            fontFamily: baseFont,
                          }}
                        >
                          No keywords added
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {/* ── Platform Priority Tab ── */}
            {activeTab === 1 && (
              <Box style={{ maxWidth: "560px" }}>
                {/* Available platform pool */}
                <Box style={sectionStyle}>
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: "10px",
                      marginBottom: "10px",
                    }}
                  >
                    <Typography style={sectionLabel}>
                      Available Platforms
                    </Typography>
                    <Typography
                      style={{
                        fontSize: "12px",
                        color: "#9ca3af",
                        fontFamily: baseFont,
                      }}
                    >
                      tap to add to priority list
                    </Typography>
                  </Box>

                  <Box
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      padding: "14px",
                      backgroundColor: "#fafafa",
                      border: "1px dashed rgba(0,0,0,0.12)",
                      borderRadius: "12px",
                      minHeight: "56px",
                      alignItems: "center",
                    }}
                  >
                    {availablePlatforms.length > 0 ? (
                      availablePlatforms.map((p) => {
                        const meta = getPlatformMeta(p);
                        return (
                          <Box
                            key={p}
                            role="button"
                            onClick={() => handleAddPlatform(p)}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "7px 12px 7px 10px",
                              borderRadius: "999px",
                              backgroundColor: meta.tint,
                              border: `1px solid ${meta.border}`,
                              cursor: isLocked ? "not-allowed" : "pointer",
                              opacity: isLocked ? 0.55 : 1,
                              userSelect: "none",
                              transition:
                                "transform 0.15s ease, box-shadow 0.15s ease",
                            }}
                            onMouseOver={(e) => {
                              if (isLocked) return;
                              e.currentTarget.style.transform =
                                "translateY(-1px)";
                              e.currentTarget.style.boxShadow =
                                "0px 3px 8px rgba(17,24,39,0.08)";
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "none";
                            }}
                          >
                            <FontAwesomeIcon
                              icon={meta.icon}
                              style={{
                                color: meta.accent,
                                fontSize: "15px",
                              }}
                            />
                            <Typography
                              style={{
                                fontFamily: baseFont,
                                fontSize: "13px",
                                fontWeight: 600,
                                color: meta.accent,
                              }}
                            >
                              {meta.label}
                            </Typography>
                            <AddIcon
                              style={{
                                fontSize: "15px",
                                color: meta.accent,
                                opacity: 0.7,
                              }}
                            />
                          </Box>
                        );
                      })
                    ) : (
                      <Typography
                        style={{
                          fontSize: "13px",
                          color: "#9ca3af",
                          fontStyle: "italic",
                          fontFamily: baseFont,
                        }}
                      >
                        All platforms added
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Priority Order List */}
                {platformPriorityOrder.length > 0 ? (
                  <Box style={sectionStyle}>
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: "10px",
                        marginBottom: "14px",
                      }}
                    >
                      <Typography style={sectionLabel}>
                        Priority Order
                      </Typography>
                      <Typography
                        style={{
                          fontSize: "12px",
                          color: "#9ca3af",
                          fontFamily: baseFont,
                        }}
                      >
                        drag to reorder · top = highest priority
                      </Typography>
                    </Box>

                    <Box
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      {platformPriorityOrder.map((platform, index) => {
                        const meta = getPlatformMeta(platform);
                        const isTop = index === 0;
                        const isDragOver =
                          dragOverIndex === index && draggingIndex !== index;
                        const isDragging = draggingIndex === index;
                        return (
                          <Box
                            key={platform}
                            draggable={!isLocked}
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDrop={() => handleDrop(index)}
                            onDragEnd={() => {
                              dragIndexRef.current = null;
                              setDragOverIndex(null);
                              setDraggingIndex(null);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "14px",
                              padding: "12px 14px",
                              backgroundColor: meta.tint,
                              border: `1px solid ${
                                isDragOver ? meta.accent : meta.border
                              }`,
                              borderRadius: "12px",
                              cursor: isLocked ? "not-allowed" : "grab",
                              boxShadow: isDragging
                                ? "0px 8px 20px rgba(17,24,39,0.12)"
                                : "0px 1px 2px rgba(17,24,39,0.03)",
                              transform: isDragOver
                                ? "translateY(1px)"
                                : "none",
                              transition:
                                "border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
                              userSelect: "none",
                              opacity: isLocked ? 0.6 : isDragging ? 0.55 : 1,
                            }}
                          >
                            {/* Drag handle */}
                            <DragIndicatorIcon
                              style={{
                                color: meta.accent,
                                opacity: 0.45,
                                fontSize: "20px",
                                flexShrink: 0,
                              }}
                            />

                            {/* Rank pill */}
                            <Box
                              style={{
                                minWidth: 22,
                                height: 22,
                                padding: "0 6px",
                                borderRadius: "6px",
                                backgroundColor: "#ffffff",
                                color: meta.accent,
                                border: `1px solid ${meta.border}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "11px",
                                fontWeight: 700,
                                flexShrink: 0,
                                fontFamily: baseFont,
                              }}
                            >
                              {index + 1}
                            </Box>

                            {/* Platform icon */}
                            <Box
                              style={{
                                width: 30,
                                height: 30,
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#ffffff",
                                border: `1px solid ${meta.border}`,
                                flexShrink: 0,
                              }}
                            >
                              <FontAwesomeIcon
                                icon={meta.icon}
                                style={{
                                  color: meta.accent,
                                  fontSize: "16px",
                                }}
                              />
                            </Box>

                            {/* Label */}
                            <Typography
                              style={{
                                flex: 1,
                                fontFamily: baseFont,
                                fontSize: "14px",
                                fontWeight: 600,
                                color: meta.accent,
                                letterSpacing: "-0.005em",
                              }}
                            >
                              {meta.label}
                            </Typography>

                            {/* Top priority tag */}
                            {isTop && (
                              <Typography
                                style={{
                                  fontSize: "10px",
                                  fontWeight: 700,
                                  letterSpacing: "0.06em",
                                  textTransform: "uppercase",
                                  color: meta.accent,
                                  backgroundColor: "#ffffff",
                                  border: `1px solid ${meta.border}`,
                                  padding: "3px 8px",
                                  borderRadius: "999px",
                                  flexShrink: 0,
                                  fontFamily: baseFont,
                                }}
                              >
                                Primary
                              </Typography>
                            )}

                            {/* Remove → back to pool */}
                            <Button
                              onClick={() => handleRemovePlatform(platform)}
                              disabled={isLocked}
                              style={{
                                minWidth: "auto",
                                padding: "4px",
                                borderRadius: "8px",
                                color: meta.accent,
                                opacity: 0.7,
                                flexShrink: 0,
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "rgba(255,255,255,0.6)";
                                e.currentTarget.style.opacity = "1";
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                                e.currentTarget.style.opacity = "0.7";
                              }}
                            >
                              <CloseIcon style={{ fontSize: "17px" }} />
                            </Button>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ) : (
                  <Typography
                    style={{
                      color: "#9ca3af",
                      fontFamily: baseFont,
                      fontSize: "13px",
                      fontStyle: "italic",
                    }}
                  >
                    Tap a platform above to start building your priority order.
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box
            style={{
              ...footerStyle,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* 🔹 Left side: Delete Button */}
            <Button
              variant="outlined"
              onClick={() => setOpenDelete(true)}
              disabled={isLocked}
              style={outlinedBtn(isLoading || !!loadError, true)}
              onMouseOver={(e) => {
                if (!isLoading && !loadError) {
                  e.currentTarget.style.backgroundColor = "#fee2e2";
                  e.currentTarget.style.borderColor = "#dc2626";
                  e.currentTarget.style.color = "#dc2626";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow =
                    "0px 4px 12px rgba(239,68,68,0.15)";
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.borderColor = "#ef4444";
                e.currentTarget.style.color = "#ef4444";
                e.currentTarget.style.transform = "translateY(0px)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              Delete
            </Button>

            {/* 🔹 Right side: Cancel + Save */}
            <Box style={{ display: "flex", gap: "10px" }}>
              <Button
                variant="outlined"
                onClick={handleClose}
                style={outlinedBtn(false)}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#f0f9ff";
                  e.currentTarget.style.borderColor = "#5855eb";
                  e.currentTarget.style.color = "#5855eb";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow =
                    "0px 4px 12px rgba(99,102,241,0.15)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.borderColor = "#6366f1";
                  e.currentTarget.style.color = "#6366f1";
                  e.currentTarget.style.transform = "translateY(0px)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleSave}
                startIcon={!isLoading ? <SaveIcon /> : null}
                disabled={isLocked}
                style={containedBtn(!hasChanges() || isLoading || !!loadError)}
                onMouseOver={(e) => {
                  if (hasChanges() && !isLoading && !loadError) {
                    e.currentTarget.style.backgroundColor = "#5855eb";
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow =
                      "0px 6px 16px rgba(99,102,241,0.4)";
                  }
                }}
                onMouseOut={(e) => {
                  if (hasChanges() && !isLoading && !loadError) {
                    e.currentTarget.style.backgroundColor = "#6366f1";
                    e.currentTarget.style.transform = "translateY(0px)";
                    e.currentTarget.style.boxShadow =
                      "0px 4px 12px rgba(99,102,241,0.3)";
                  }
                }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={18} style={{ color: "#ffffff" }} />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <DeleteTopicDialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        sessionData={sessionData}
        token={token}
        // onSave={fetchUpdatedTopics}
      />
    </>
  );
};

export default EditModal;
